import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ColumnRow, EkoFabAction, EkoFabSpeedDialComponent, EkoGenericTableComponent, TableConfig } from '@ekowitryna/ui';
import {
  KobizeReportCreateDto,
  KobizeReportDto,
  ReportsControllerService,
  SubstancesControllerService,
  type KobizeReportUpdateDto
} from '@mobiletract/kobize-service-angular-client';
import { TableLazyLoadEvent } from 'primeng/table';
import { FilterMatchMode, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { CompanyContextService } from '../../core/services/company-context.service';

interface ReportRow {
  id: string;
  companyId: string;
  nazwa: string;
  rok: number;
  opis: string;
  createdAt: string;
  dataUtworzeniaTekst: string;
}

interface YearOption {
  label: string;
  value: number;
}

type ReportDialogMode =
  | { kind: 'create' }
  | { kind: 'edit'; reportId: string }
  | { kind: 'copy'; sourceReportId: string };

const REPORT_COLUMN_HEADER_KEYS = [
  'reports.columns.name',
  'reports.columns.year',
  'reports.columns.description',
  'reports.columns.createdAt'
] as const;

const REPORT_TITLE_MIN_LENGTH = 3;
const REPORT_TITLE_MAX_LENGTH = 100;
const REPORTS_TABLE_STATE_KEY = 'kobize_table-reports-v3';
const DEFAULT_PAGE_SIZE = 10;

interface PersistedTableState {
  first?: number;
  rows?: number;
  sortField?: string;
  sortOrder?: number;
}

const DEFAULT_API_SORT = 'year';
const DEFAULT_API_DIRECTION = 'desc' as const;

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    EkoFabSpeedDialComponent,
    EkoGenericTableComponent,
    TableModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    ToastModule,
    InputTextModule,
    TextareaModule,
    SelectModule
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css'
})
export class ReportsComponent implements OnInit {
  private readonly reportsApi = inject(ReportsControllerService);
  private readonly substancesApi = inject(SubstancesControllerService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companyContext = inject(CompanyContextService);
  private readonly router = inject(Router);

  @ViewChild('table') table!: EkoGenericTableComponent;

  private getActiveCompanyId(): string | null {
    return this.companyContext.currentCompanyId;
  }

  yearOptions: YearOption[] = this.buildYearOptionsFallback();
  yearsLoading = false;

  columnRows: ColumnRow[] = [];
  tableConfig: TableConfig = {
    paginator: true,
    rows: 10,
    loading: false,
    totalRecords: 0,
    filterable: true,
    editable: false,
    actionsPosition: 'right',
    first: 0,
    responsiveLayout: 'scroll',
    editMode: 'row',
    tableMenuItems: [],
    dataKey: 'id',
    reorderableColumns: true,
    resizableColumns: true,
    selectionMode: 'multiple',
    stateKey: REPORTS_TABLE_STATE_KEY
  };

  createDialogVisible = false;
  createForm!: FormGroup;
  createSaving = false;
  deleteDialogVisible = false;
  deleteSaving = false;
  rowPendingDelete: ReportRow | null = null;
  private reportDialogMode: ReportDialogMode = { kind: 'create' };
  private lastLoadedCompanyId: string | null | undefined;

  constructor() {
    this.initCreateForm();
  }

  ngOnInit(): void {
    this.restoreTableConfigFromStorage();
    this.setupColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setupColumns());
    this.companyContext.selectedCompanyId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyId) => {
        void this.loadAvailableYears(companyId);
        const previous = this.lastLoadedCompanyId;
        if (previous !== undefined && previous !== companyId) {
          this.table?.reload(true);
        } else if (
          companyId &&
          previous === undefined &&
          this.table?.isInitialized &&
          this.table.data.length === 0 &&
          !this.table.tableConfig.loading
        ) {
          this.table.reload();
        }
        if (companyId !== null) {
          this.lastLoadedCompanyId = companyId;
        }
      });
  }

  private readPersistedTableState(): PersistedTableState | null {
    try {
      const raw = localStorage.getItem(REPORTS_TABLE_STATE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as PersistedTableState;
    } catch {
      return null;
    }
  }

  private hasPersistedUserSort(state: PersistedTableState | null): state is PersistedTableState & {
    sortField: string;
    sortOrder: number;
  } {
    return (
      state?.sortField != null &&
      state.sortField !== '' &&
      state.sortOrder != null &&
      state.sortOrder !== 0
    );
  }

  private restoreTableConfigFromStorage(): void {
    const state = this.readPersistedTableState();
    if (!state) {
      this.tableConfig.rows = DEFAULT_PAGE_SIZE;
      this.tableConfig.first = 0;
      delete this.tableConfig['defaultSortField'];
      delete this.tableConfig['defaultSortOrder'];
      return;
    }
    this.tableConfig.rows =
      state.rows != null && state.rows > 0 ? state.rows : DEFAULT_PAGE_SIZE;
    this.tableConfig.first = state.first != null && state.first >= 0 ? state.first : 0;
    if (this.hasPersistedUserSort(state)) {
      this.tableConfig['defaultSortField'] = state.sortField;
      this.tableConfig['defaultSortOrder'] = state.sortOrder;
    } else {
      delete this.tableConfig['defaultSortField'];
      delete this.tableConfig['defaultSortOrder'];
    }
  }

  private buildYearOptionsFallback(): YearOption[] {
    const y = new Date().getFullYear();
    const from = y - 12;
    const to = y + 2;
    const opts: YearOption[] = [];
    for (let year = from; year <= to; year++) {
      opts.push({ label: String(year), value: year });
    }
    return opts;
  }

  private getDefaultYear(): number {
    if (this.yearOptions.length > 0) {
      return this.yearOptions[this.yearOptions.length - 1].value;
    }
    return new Date().getFullYear();
  }

  private syncFormYearWithAvailableOptions(): void {
    const yearControl = this.createForm.get('year');
    if (!yearControl) {
      return;
    }

    const currentYear = Number(yearControl.value);
    const isCurrentYearAvailable = this.yearOptions.some((option) => option.value === currentYear);
    if (!isCurrentYearAvailable) {
      yearControl.setValue(this.getDefaultYear(), { emitEvent: false });
    }
  }

  private async loadAvailableYears(companyId: string | null): Promise<void> {
    if (!companyId) {
      this.yearOptions = this.buildYearOptionsFallback();
      this.syncFormYearWithAvailableOptions();
      return;
    }

    this.yearsLoading = true;
    try {
      const years = await firstValueFrom(this.substancesApi.getAnnualRateYears(companyId));
      const normalizedYears = [...new Set((years ?? []).filter((year: number) => Number.isInteger(year)))]
        .map((year) => Number(year))
        .sort((a, b) => a - b);
      this.yearOptions =
        normalizedYears.length > 0
          ? normalizedYears.map((year) => ({ label: String(year), value: year }))
          : this.buildYearOptionsFallback();
    } catch (error: unknown) {
      this.yearOptions = this.buildYearOptionsFallback();
      const detail =
        this.mapHttpErrorDetail(error) ?? this.translate.instant('reports.toast.loadListFailed');
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.yearsLoading = false;
      this.syncFormYearWithAvailableOptions();
    }
  }

  private initCreateForm(): void {
    const defaultYear = this.getDefaultYear();
    this.createForm = new FormGroup({
      title: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(REPORT_TITLE_MIN_LENGTH),
          Validators.maxLength(REPORT_TITLE_MAX_LENGTH)
        ]
      }),
      year: new FormControl(defaultYear, { nonNullable: true, validators: [Validators.required] }),
      description: new FormControl('', { nonNullable: true })
    });
  }


  private formatCreatedAtYyyyMmDd(raw: string | Date | undefined): string | null {
    if (raw == null) {
      return null;
    }
    if (raw instanceof Date) {
      if (Number.isNaN(raw.getTime())) {
        return null;
      }
      const y = raw.getFullYear();
      const m = String(raw.getMonth() + 1).padStart(2, '0');
      const day = String(raw.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    const s = String(raw).trim();
    if (s === '') {
      return null;
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private mapDtoToRow(dto: KobizeReportDto): ReportRow {
    const raw = dto.createdAt;
    const formatted = this.formatCreatedAtYyyyMmDd(raw);
    const dataUtworzeniaTekst =
      formatted ?? this.translate.instant('reports.common.noDate');

    return {
      id: dto.id ?? '',
      companyId: dto.companyId ?? this.getActiveCompanyId() ?? '',
      nazwa: dto.title,
      rok: dto.year,
      opis: dto.description ?? '',
      createdAt: raw != null ? String(raw) : '',
      dataUtworzeniaTekst
    };
  }

  private resolveSort(event: TableLazyLoadEvent): { sort: string; direction: 'asc' | 'desc' } {
    const sortField = event.sortField as string | undefined;
    const hasUserSort =
      sortField != null &&
      sortField !== '' &&
      event.sortOrder != null &&
      event.sortOrder !== 0;
    if (!hasUserSort) {
      return { sort: DEFAULT_API_SORT, direction: DEFAULT_API_DIRECTION };
    }
    const sort = this.mapUiSortToApi(sortField) ?? DEFAULT_API_SORT;
    const direction: 'asc' | 'desc' = event.sortOrder === 1 ? 'asc' : 'desc';
    return { sort, direction };
  }

  private mapUiSortToApi(sortField: string | undefined): string | undefined {
    if (!sortField) {
      return undefined;
    }
    const apiField: Record<string, string> = {
      nazwa: 'title',
      rok: 'year',
      opis: 'description',
      createdAt: 'createdAt'
    };
    return apiField[sortField];
  }

  private setupColumns(): void {
    const t = this.translate.instant([...REPORT_COLUMN_HEADER_KEYS]) as Record<string, string>;
    this.columnRows = [
      {
        columns: [
          {
            field: 'nazwa',
            type: 'text',
            load: (entry: ReportRow) => entry.nazwa,
            header: t['reports.columns.name'],
            resizable: true,
            reorderable: true,
            sortable: { enabled: true, field: 'nazwa' },
            filterable: {
              type: 'text',
              showMatchModes: false,
              showOperator: false,
              showAddButton: false,
              field: 'nazwa',
              defaultMatchMode: FilterMatchMode.CONTAINS,
              matchMode: FilterMatchMode.CONTAINS,
              availableMatchModes: [FilterMatchMode.CONTAINS]
            }
          },
          {
            field: 'rok',
            type: 'number',
            load: (entry: ReportRow) => entry.rok,
            header: t['reports.columns.year'],
            resizable: true,
            reorderable: true,
            sortable: { enabled: true, field: 'rok' }
          },
          {
            field: 'opis',
            type: 'text',
            load: (entry: ReportRow) => entry.opis,
            header: t['reports.columns.description'],
            resizable: true,
            reorderable: true,
            sortable: { enabled: true, field: 'opis' }
          },
          {
            field: 'dataUtworzeniaTekst',
            type: 'text',
            load: (entry: ReportRow) => entry.dataUtworzeniaTekst,
            header: t['reports.columns.createdAt'],
            resizable: true,
            reorderable: true,
            sortable: { enabled: true, field: 'createdAt' }
          }
        ]
      }
    ];
  }

  dataProvider = async (
    event: TableLazyLoadEvent
  ): Promise<{ data: ReportRow[]; totalRecords: number }> => {
    try {
      const rows = event.rows ?? 10;
      const first = event.first ?? 0;
      const page = Math.floor(first / rows);

      let title: string | undefined;
      const nameFilter = event.filters?.['nazwa'];
      if (nameFilter && Array.isArray(nameFilter)) {
        const raw = (nameFilter[0] as { value?: string })?.value;
        const trimmed = raw != null ? String(raw).trim() : '';
        if (trimmed !== '') {
          title = trimmed;
        }
      }

      const { sort, direction } = this.resolveSort(event);
      const companyId = this.getActiveCompanyId();
      if (!companyId) {
        return {
          data: [],
          totalRecords: 0
        };
      }

      const response = await firstValueFrom(
        this.reportsApi.getAllReports(
          title,
          undefined,
          companyId,
          undefined,
          undefined,
          page,
          rows,
          sort,
          direction
        )
      );

      const content = response?.content ?? [];
      const total = response?.totalElements ?? 0;
      const data = content.map((dto) => this.mapDtoToRow(dto));

      return {
        data,
        totalRecords: total
      };
    } catch (error) {
      const detail =
        this.mapHttpErrorDetail(error) ?? this.translate.instant('reports.toast.loadListFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
      return Promise.reject(error);
    }
  };

  get pageActions(): EkoFabAction[] {
    return [
      {
        id: 'add-report',
        label: this.translate.instant('reports.addReport'),
        icon: 'pi pi-plus',
        command: () => this.onAddReport(),
      },
    ];
  }

  onAddReport(): void {
    this.reportDialogMode = { kind: 'create' };
    this.createForm.reset({
      title: '',
      year: this.getDefaultYear(),
      description: ''
    });
    this.createDialogVisible = true;
  }

  getCreateFieldError(controlName: 'title' | 'year' | 'description'): string | null {
    const c = this.createForm.get(controlName);
    if (!c?.touched || !c.invalid) {
      return null;
    }
    if (c.errors?.['required']) {
      return this.translate.instant('reports.form.fieldRequired');
    }
    if (c.errors?.['minlength']) {
      return this.translate.instant('reports.form.titleMinLength', {
        min: REPORT_TITLE_MIN_LENGTH
      });
    }
    if (c.errors?.['maxlength']) {
      return this.translate.instant('reports.form.titleMaxLength', {
        max: REPORT_TITLE_MAX_LENGTH
      });
    }
    return null;
  }

  /**
   * Buduje DTO zgodne z klientem OpenAPI: companyId w body musi być zgodne z parametrem query przy createReport.
   */
  private buildCreateReportDto(raw: {
    title: string;
    year: number;
    description: string;
    companyId: string;
  }): KobizeReportCreateDto {
    const title = String(raw.title).trim();
    const year = Number(raw.year);
    const descriptionTrimmed = String(raw.description ?? '').trim();
    return {
      title,
      year,
      companyId: raw.companyId,
      ...(descriptionTrimmed !== '' ? { description: descriptionTrimmed } : {})
    };
  }

  private buildUpdateReportDto(raw: { title: string; year: number; description: string }): KobizeReportUpdateDto {
    const title = String(raw.title).trim();
    const year = Number(raw.year);
    const descriptionTrimmed = String(raw.description ?? '').trim();
    return {
      title,
      year,
      ...(descriptionTrimmed !== '' ? { description: descriptionTrimmed } : {})
    };
  }

  /** Pokazuje tylko `message` z odpowiedzi serwera; fallback gdy brak `message`. */
  private mapHttpErrorDetail(err: unknown): string {
    const fallback = this.translate.instant('common.somethingWentWrong');
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }
    const body = err.error;
    if (body && typeof body === 'object') {
      const msg = (body as Record<string, unknown>)['message'];
      if (typeof msg === 'string' && msg.trim().length > 0) {
        return msg.trim();
      }
      return fallback;
    }
    if (typeof body === 'string') {
      const text = body.trim();
      if (text.length === 0) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const msg = parsed['message'];
        if (typeof msg === 'string' && msg.trim().length > 0) {
          return msg.trim();
        }
      } catch {
        return fallback;
      }
    }
    return fallback;
  }

  async onSaveCreateReport(): Promise<void> {
    if (this.createSaving) {
      return;
    }
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const companyId = this.getActiveCompanyId();
    if (!companyId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail: 'Brak wybranego podmiotu.'
      });
      return;
    }
    const dto = this.buildCreateReportDto({
      ...this.createForm.getRawValue(),
      companyId
    });

    if (!dto.title) {
      this.createForm.get('title')?.setErrors({ required: true });
      this.createForm.get('title')?.markAsTouched();
      return;
    }
    if (dto.title.length < REPORT_TITLE_MIN_LENGTH) {
      this.createForm.get('title')?.setErrors({
        minlength: {
          requiredLength: REPORT_TITLE_MIN_LENGTH,
          actualLength: dto.title.length
        }
      });
      this.createForm.get('title')?.markAsTouched();
      return;
    }
    if (dto.title.length > REPORT_TITLE_MAX_LENGTH) {
      this.createForm.get('title')?.setErrors({
        maxlength: {
          requiredLength: REPORT_TITLE_MAX_LENGTH,
          actualLength: dto.title.length
        }
      });
      this.createForm.get('title')?.markAsTouched();
      return;
    }

    const mode = this.reportDialogMode;
    const isCopy = mode.kind === 'copy';
    const isEdit = mode.kind === 'edit';
    const updateDto = isEdit
      ? this.buildUpdateReportDto(this.createForm.getRawValue())
      : null;

    if (isEdit && updateDto) {
      if (!updateDto.title) {
        this.createForm.get('title')?.setErrors({ required: true });
        this.createForm.get('title')?.markAsTouched();
        return;
      }
      if (updateDto.title.length < REPORT_TITLE_MIN_LENGTH) {
        this.createForm.get('title')?.setErrors({
          minlength: {
            requiredLength: REPORT_TITLE_MIN_LENGTH,
            actualLength: updateDto.title.length
          }
        });
        this.createForm.get('title')?.markAsTouched();
        return;
      }
      if (updateDto.title.length > REPORT_TITLE_MAX_LENGTH) {
        this.createForm.get('title')?.setErrors({
          maxlength: {
            requiredLength: REPORT_TITLE_MAX_LENGTH,
            actualLength: updateDto.title.length
          }
        });
        this.createForm.get('title')?.markAsTouched();
        return;
      }
    }

    this.createSaving = true;
    this.createForm.disable({ emitEvent: false });

    try {
      if (mode.kind === 'copy') {
        await firstValueFrom(
          this.reportsApi.copyReport(mode.sourceReportId, companyId, dto)
        );
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('reports.toast.successTitle'),
          detail: this.translate.instant('reports.toast.copySuccess')
        });
      } else if (mode.kind === 'edit' && updateDto) {
        await firstValueFrom(
          this.reportsApi.updateReport(mode.reportId, companyId, updateDto)
        );
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('reports.toast.successTitle'),
          detail: this.translate.instant('reports.toast.updateSuccess')
        });
      } else {
        await firstValueFrom(this.reportsApi.createReport(companyId, dto));
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('reports.toast.successTitle'),
          detail: this.translate.instant('reports.toast.createSuccess')
        });
      }
      this.resetCreateDialogAfterSave();
      this.table?.reload(true);
    } catch (err: unknown) {
      const isCopyDbUnavailable =
        isCopy && err instanceof HttpErrorResponse && err.status === 503;

      if (isCopyDbUnavailable) {
        this.resetCreateDialogAfterSave();
        this.table?.reload(true);
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('reports.toast.warnTitle'),
          detail: this.mapHttpErrorDetail(err)
        });
        return;
      }

      const detail =
        this.mapHttpErrorDetail(err) ??
        this.translate.instant(
          isCopy
            ? 'reports.toast.copyFailed'
            : isEdit
              ? 'reports.toast.updateFailed'
              : 'reports.toast.createFailed'
        );
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.createSaving = false;
      this.createForm.enable({ emitEvent: false });
    }
  }

  onEdit(row: ReportRow): void {
    this.reportDialogMode = { kind: 'edit', reportId: row.id };
    this.createForm.reset({
      title: row.nazwa ?? '',
      year: row.rok ?? this.getDefaultYear(),
      description: row.opis ?? ''
    });
    this.createForm.markAsUntouched();
    this.createDialogVisible = true;
  }

  onCreateDialogHide(): void {
    if (!this.createSaving) {
      this.reportDialogMode = { kind: 'create' };
    }
  }

  private resetCreateDialogAfterSave(): void {
    this.reportDialogMode = { kind: 'create' };
    this.createDialogVisible = false;
    this.createForm.reset({
      title: '',
      year: this.getDefaultYear(),
      description: ''
    });
  }

  private buildSuggestedCopyTitle(sourceTitle: string): string {
    const base = String(sourceTitle ?? '').trim();
    const suffix = this.translate.instant('reports.form.copyNameSuffix');
    if (base === '') {
      return this.translate.instant('reports.form.copyDefaultTitle');
    }
    const candidate = base + suffix;
    if (candidate.length <= REPORT_TITLE_MAX_LENGTH) {
      return candidate;
    }
    const maxBase = REPORT_TITLE_MAX_LENGTH - suffix.length;
    const trimmedBase = base.slice(0, Math.max(0, maxBase)).trimEnd();
    const merged = (trimmedBase || base.slice(0, REPORT_TITLE_MIN_LENGTH)) + suffix;
    return merged.slice(0, REPORT_TITLE_MAX_LENGTH);
  }

  onCopy(row: ReportRow): void {
    this.reportDialogMode = { kind: 'copy', sourceReportId: row.id };
    this.createForm.reset({
      title: this.buildSuggestedCopyTitle(row.nazwa),
      year: row.rok ?? this.getDefaultYear(),
      description: row.opis ?? ''
    });
    this.createForm.markAsUntouched();
    this.createDialogVisible = true;
  }

  onFill(row: ReportRow): void {
    this.table?.dt?.saveState();
    void this.router.navigate(['reports', 'fill', row.id]);
  }

  onDelete(_row: ReportRow): void {
    this.rowPendingDelete = _row;
    this.deleteDialogVisible = true;
  }

  getDialogTitleKey(): string {
    switch (this.reportDialogMode.kind) {
      case 'copy':
        return 'reports.dialog.copyTitle';
      case 'edit':
        return 'reports.dialog.editTitle';
      default:
        return 'reports.dialog.createTitle';
    }
  }

  onDeleteDialogHide(): void {
    if (!this.deleteSaving) {
      this.rowPendingDelete = null;
    }
  }

  cancelDeleteReport(): void {
    if (this.deleteSaving) {
      return;
    }
    this.deleteDialogVisible = false;
    this.rowPendingDelete = null;
  }

  async confirmDeleteReport(): Promise<void> {
    if (this.deleteSaving || !this.rowPendingDelete) {
      return;
    }
    const companyId = this.getActiveCompanyId();
    if (!companyId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail: 'Brak wybranego podmiotu.'
      });
      return;
    }

    this.deleteSaving = true;
    try {
      await firstValueFrom(this.reportsApi.deleteReport(this.rowPendingDelete.id, companyId));
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('reports.toast.successTitle'),
        detail: this.translate.instant('reports.toast.deleteSuccess')
      });
      this.deleteDialogVisible = false;
      this.rowPendingDelete = null;
      this.table?.reload(true);
    } catch (err: unknown) {
      const detail =
        this.mapHttpErrorDetail(err) ?? this.translate.instant('reports.toast.deleteFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.deleteSaving = false;
    }
  }

}
